import { getMonth, getYear } from "date-fns";
import { SelectOption } from "../models/optionsFilters/Select";
import {
  HistorialPrioridadesFilters,
  IPlanificacionDetails,
  PlanificacionesAgrupadasFilters,
  PlanificacionesFilters,
  PlanificacionesMetaFilters,
  PlanificacionForm,
  PlanificacionRequestBody,
  RegistroHistorialPrioridades,
} from "../models/planificacionesERP/PlanificacionERP";
import TipoEstado from "../models/planificacionesERP/TipoEstado";
import TipoGalpon from "../models/planificacionesERP/TipoGalpon";
import TipoMes from "../models/planificacionesERP/TipoMes";
import TipoMotivo from "../models/planificacionesERP/TipoMotivo";
import TipoProceso from "../models/planificacionesERP/TipoProceso";
import { ComponenteTabla } from "../models/componentes/Componente";

/**
 * Roles que pueden leer el dashboard de planificaciones (tendencia, metas objetivo e indicadores).
 * Reflejan los `rolAuth` de esas rutas en `ERP-Electrans-Backend/src/routes/PlanificacionesERPRoutes.ts`;
 * como allá, cualquier rol que contenga OPERARIO cuenta como OPERARIO.
 */
export const DASHBOARD_PLANIFICACIONES_ROLES = ['ADMINISTRADOR', 'PLANIFICACION', 'JEFETALLER', 'JEFE CONTROL OPERACIONAL', 'SUPERVISOR', 'EXPEDITOR', 'OPERARIO', 'METROLOGIA'];

export const canVerDashboardPlanificaciones = (userRoles: string[] | undefined): boolean =>
  !!userRoles && userRoles.some((rol) => rol.includes('OPERARIO') || DASHBOARD_PLANIFICACIONES_ROLES.includes(rol));

export const MESES_PLANIFICACION = [
  "ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO",
  "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE",
];

// Una programación solo puede volver a BORRADOR si su mes todavía no empieza. El backend valida
// lo mismo; acá sirve para no mostrar el botón cuando no corresponde.
export const esMesFuturo = (mes: string, year: number, referencia: Date = new Date()): boolean => {
  const indiceMes = MESES_PLANIFICACION.indexOf(mes);

  if (indiceMes < 0) {
    return false;
  }

  return new Date(year, indiceMes, 1) > new Date(getYear(referencia), getMonth(referencia), 1);
};

// Un mes cerrado se muestra desde su respaldo histórico, así que sus prioridades ya no se ajustan.
// Mismo criterio que `isPastMonth` en el backend.
export const esMesPasado = (mes: string, year: number, referencia: Date = new Date()): boolean => {
  const indiceMes = MESES_PLANIFICACION.indexOf(mes);

  if (indiceMes < 0) {
    return false;
  }

  return new Date(year, indiceMes, 1) < new Date(getYear(referencia), getMonth(referencia), 1);
};

/**
 * Roles que pueden reordenar las prioridades del mes: los mismos que editan la prioridad de una
 * planificación desde su detalle. Reflejan el `rolAuth` de PUT /prioridades en
 * `ERP-Electrans-Backend/src/routes/PlanificacionesERPRoutes.ts`.
 */
export const AJUSTAR_PRIORIDADES_ROLES = ['ADMINISTRADOR', 'PLANIFICACION', 'JEFETALLER', 'JEFE CONTROL OPERACIONAL', 'SUPERVISOR', 'EXPEDITOR'];

export type CambioPrioridad = "sube" | "baja";

// 1 es la más prioritaria: la prioridad sube cuando el número nuevo es menor que el anterior y
// baja cuando es mayor. Sin prioridad anterior no hay con qué comparar.
export const getCambioPrioridad = (anterior: number | null | undefined, nueva: number): CambioPrioridad | undefined => {
  if (typeof anterior !== "number" || anterior === nueva) {
    return undefined;
  }
  return nueva < anterior ? "sube" : "baja";
};

export const getEmptyHistorialPrioridadesFilters = (): HistorialPrioridadesFilters => ({
  codigoOP: "",
  fechaInicio: "",
  fechaTermino: "",
  page: 1,
});

export const getOrigenCambioPrioridadTexto = (registro: RegistroHistorialPrioridades): string => {
  switch (registro.origen) {
    case "AJUSTE_MES":
      return `Ajuste de prioridades ${registro.mes} ${registro.year}`;
    case "PROGRAMACION":
      return `Programación ${registro.mes} ${registro.year}`;
    default:
      return "Edición de la planificación";
  }
};

/**
 * Al abrir el detalle de una planificación se manda en `location.state` la URL de la vista de origen
 * (pestaña incluida), para que el breadcrumb y el borrado vuelvan ahí y no siempre al módulo
 * general: el detalle lo comparten Planificaciones y Planificaciones de Laboratorio.
 */
export interface EstadoOrigenPlanificacion {
  volverA: string;
}

const ORIGENES_PLANIFICACION = [
  { ruta: '/laboratorio/planificaciones', label: 'Planificaciones Laboratorio' },
  { ruta: '/planificacionesERP', label: 'Planificaciones' },
  { ruta: '/8-dimensiones', label: '8 Dimensiones' },
];

export const getEstadoOrigenPlanificacion = (location: { pathname: string, search: string }): EstadoOrigenPlanificacion => ({
  volverA: `${location.pathname}${location.search}`,
});

// Solo se respeta un origen conocido; cualquier otro (VPI, solicitudes, un link directo) vuelve al
// módulo general como antes.
export const getOrigenPlanificacion = (state: unknown): { href: string, label: string } => {
  const volverA = (state as Partial<EstadoOrigenPlanificacion> | undefined)?.volverA;
  const origen = volverA ? ORIGENES_PLANIFICACION.find((o) => volverA.split('?')[0] === o.ruta) : undefined;
  return origen && volverA
    ? { href: volverA, label: origen.label }
    : { href: '/planificacionesERP', label: 'Planificaciones' };
};

export const getEmptyPlanificacionesFilters = (module?: string, mes?: string, year?: number): PlanificacionesFilters => {
  const isPlanificacionMensual = !!mes && !!year;
  let filters;
  filters = {
    // tipo: "",
    prioridad: "asc",
    proceso: "",
    codigoOP: "",
    cliente: "",
    modeloComponente: "",
    fechaInicio: "",
    fechaTermino: "",
    fechaInicioReal: "",
    fechaTerminoReal: "",
    // galpon: "",
    // estado: "",
    mes: mes || "",
    year: year,
    isMeta: isPlanificacionMensual ? 'true' : '',
    page: 1,
  }
  if (module === 'subElementos') {
    filters = { ...filters, estados: ['EN PROCESO', 'DETENIDO'] };
  }
  if (isPlanificacionMensual) {
    filters = { ...filters, isPlanificacionMensual: isPlanificacionMensual };
  }
  return filters;
};

export const getEmptyPlanificacionesAgrupadasFilters = (fecha?: boolean): PlanificacionesAgrupadasFilters => {
  const fechaActual = new Date();
  const year = getYear(fechaActual);
  const mes = getMonth(fechaActual);

  const mesParameter = [
    { mes: 'ENERO', nro: 0 },
    { mes: "FEBRERO", nro: 1 },
    { mes: "MARZO", nro: 2 },
    { mes: "ABRIL", nro: 3 },
    { mes: "MAYO", nro: 4 },
    { mes: "JUNIO", nro: 5 },
    { mes: "JULIO", nro: 6 },
    { mes: "AGOSTO", nro: 7 },
    { mes: "SEPTIEMBRE", nro: 8 },
    { mes: "OCTUBRE", nro: 9 },
    { mes: "NOVIEMBRE", nro: 10 },
    { mes: "DICIEMBRE", nro: 11 }
  ].find((month) => month.nro === mes);

  let filters;
  filters = {
    mes: undefined,
    year: undefined,
    page: 1,
  };
  
  if (fecha) {
    filters = { ...filters, mes: mesParameter?.mes, year: year };
  }

  return filters;
};

export const getEmptyPlanificacionesMetaFilters = (): PlanificacionesMetaFilters => ({
  mes: undefined,
  year: undefined,
});

export const getEmptyPlanificacionForm = (): PlanificacionForm => ({
  prioridad: 0,
  valorReparacion: 0,
});

export const formatPlanificacionRequestBody = (
  ordenProceso: string,
  planificacionForm: PlanificacionForm,
  componente?: ComponenteTabla
): PlanificacionRequestBody => ({
  prioridad: planificacionForm.prioridad,
  proceso: planificacionForm.proceso?.value.proceso,
  codigoOP: ordenProceso,
  motivoReparacion: planificacionForm.motivoReparacion?.value.motivo,
  valorReparacion: planificacionForm?.valorReparacion,
  galpon: planificacionForm.galpon?.value.galpon,
  isMeta: planificacionForm.isMeta,
  fechaInicioPlanificada: planificacionForm.fechaInicioPlanificada,
  fechaTerminoPlanificada: planificacionForm.fechaTerminoPlanificada,
  totalTareas: planificacionForm.totalTareas,
  avanceTareas: planificacionForm.avanceTareas,
  tipo: componente ? componente.tipoComponente : undefined,
  modelo: componente ? componente.modelo : undefined,
  descripcion: componente ? componente.descripcion : undefined,
});

export const formatPlanificacion = (
  id: string,
  planificacionForm?: PlanificacionForm,
  codigoOP?: string,
  isAdmin?: boolean,
): PlanificacionRequestBody => {
  if (planificacionForm) {
    return {
      _id: id,
      prioridad: planificacionForm.prioridad,
      proceso: planificacionForm.proceso?.value.proceso,
      motivoReparacion: planificacionForm.motivoReparacion?.value.motivo,
      valorReparacion: planificacionForm?.valorReparacion,
      galpon: planificacionForm.galpon?.value.galpon,
      isMeta: planificacionForm.isMeta,
      estado: planificacionForm.estado?.value.estado,
      totalTareas: planificacionForm.totalTareas,
      avanceTareas: planificacionForm.avanceTareas,
      fechaInicioPlanificada: planificacionForm.fechaInicioPlanificada,
      fechaTerminoPlanificada: planificacionForm.fechaTerminoPlanificada,
      ...(isAdmin && {
        fechaInicioReal: planificacionForm.fechaInicioReal,
        fechaTerminoReal: planificacionForm.fechaTerminoReal,
      }),
    }
  } else {
    return {
      _id: id,
      codigoOP: codigoOP,
    }
  }
};

export const formatPlanificacionObs = (
  id: string,
  planificacion: IPlanificacionDetails,
  observacion: string
): PlanificacionRequestBody => ({
  _id: id,
  isMeta: planificacion.isMeta,
  observacion: observacion,
  fechaInicioPlanificada: planificacion.fechaInicioPlanificada,
  fechaTerminoPlanificada: planificacion.fechaTerminoPlanificada,
});

export const formatPlanificacionSubcomponente = (
  id: string,
  planificacion: IPlanificacionDetails,
  subElementos: { elemento: string, opcion: string }[],
): PlanificacionRequestBody => ({
  _id: id,
  isMeta: planificacion.isMeta,
  subElementos: subElementos,
  fechaInicioPlanificada: planificacion.fechaInicioPlanificada,
  fechaTerminoPlanificada: planificacion.fechaTerminoPlanificada,
});

export const formatPlanificacionForm = (
  planificacion: IPlanificacionDetails,
  galponPlanificacionOptions: SelectOption<TipoGalpon>[],
  procesoPlanificacionOptions: SelectOption<TipoProceso>[],
  motivoReparacionOptions: SelectOption<TipoMotivo>[],
  mesPlanificacionOptions: SelectOption<TipoMes>[],
  estadosPlanificacionOptions: SelectOption<TipoEstado>[],
): PlanificacionForm => {
  const galpon = galponPlanificacionOptions.find(
    (galpon) => galpon.value.galpon === planificacion.galpon
  );
  const proceso = procesoPlanificacionOptions.find(
    (proceso) => proceso.value.proceso === planificacion.proceso
  );
  const motivoReparacion = motivoReparacionOptions.find(
    (motivo) => motivo.value.motivo === planificacion.motivoReparacion
  );
  const estado = estadosPlanificacionOptions.find((estado) => estado.value.estado === planificacion.estado);

  return {
    prioridad: planificacion.prioridad,
    valorReparacion: planificacion.valorReparacion,
    galpon: galpon,
    proceso: proceso,
    motivoReparacion: motivoReparacion,
    isMeta: planificacion.isMeta,
    estado: estado,
    totalTareas: planificacion.totalTareas,
    avanceTareas: planificacion.avanceTareas,
    fechaInicioPlanificada: planificacion.fechaInicioPlanificada,
    fechaTerminoPlanificada: planificacion.fechaTerminoPlanificada,
    fechaInicioReal: planificacion.fechaInicioReal,
    fechaTerminoReal: planificacion.fechaTerminoReal,
  };
};
